import { CopySVG } from 'common/assets';
import { themedStyled } from 'common/styles/styletron.theme';
import DeleteButton from 'components/common/delete-icon-button';
import Separator from 'components/common/separator';
import { FormState, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import { TextLabelPlacementEnum } from 'components/elements/custom-form-control';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import HotelSelectInput from 'legacy-modules/master/hotel/components/select-input';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  SectionContainer as RawSectionContainer,
  RowBetweenStyles,
  SeparatorHorizontal,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import ScheduleAddOnsTable from './add-ons-table';
import ScheduleGuestTable from './guest-table';
import ScheduleAirlineTable from './hotel-table';
import { ScheduleCardHeader, ScheduleCardHeaderLeft } from '../../style';
import { HotelSaleFormType } from '../form-type';

interface Props {
  parentIndex: number;
  onRemove: () => void;
  fieldLength?: number;
}

// function transformer(item: any): Option {
//   return {
//     id: item.id,
//     label: `${item.type} (${item.size})`,
//   };
// }

export default function ScheduleCard(props: Props) {
  const { t } = useTranslation();
  const { getValues, setValue, trigger, control } =
    useFormContext<HotelSaleFormType>();

  const name = 'hotelSales' as const;

  const { editable } = useFormState();

  const onDuplicate = React.useCallback(() => {
    const hotelSales = getValues(name);
    const hotelSale = hotelSales[props.parentIndex];
    setValue(name, [...hotelSales, hotelSale]);
  }, [getValues, props.parentIndex, setValue]);

  const hotelId = getValues(`hotelSales.${props.parentIndex}.hotelId`);
  React.useEffect(() => {
    if (!hotelId || hotelId === '') {
      setValue(`hotelSales.${props.parentIndex}.hotelName`, '');
      setValue(`hotelSales.${props.parentIndex}.hotelPhone`, '');
      setValue(`hotelSales.${props.parentIndex}.hotelEmail`, '');
      setValue(`hotelSales.${props.parentIndex}.hotelAddress`, '');
      setValue(`hotelSales.${props.parentIndex}.defaultCheckInOut`, {
        in: '',
        out: '',
      });
    }
  }, [hotelId, props.parentIndex, setValue]);

  const { isCityTax } = useWatch({
    name: `${name}.${props.parentIndex}`,
    control,
  });

  return (
    <CardSection style={{ paddingBottom: 0 }}>
      <ScheduleCardHeader>
        <ScheduleCardHeaderLeft>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 16,
              alignItems: 'center',
            }}
          >
            <Text variant="HeadingXSmall">
              {t('ticketSales:schedule_extra', {
                extra: props.parentIndex + 1,
              })}
            </Text>
            {editable && (
              <Button
                variant="default"
                startEnhancer={(color, size) => (
                  <CopySVG color={color} size={size} />
                )}
                buttonType="tertiary"
                onClick={onDuplicate}
              >
                {t('common:duplicate_schedule')}
              </Button>
            )}
          </div>
          <Input
            type="text"
            name={`hotelSales.${props.parentIndex}.bookingCode`}
            label={t('common:booking_code')}
            labelPlacement={TextLabelPlacementEnum.Left}
            placeholder={t('common:booking_code')}
            noMargin
          />
        </ScheduleCardHeaderLeft>
        <FormState>
          {({ editable }) =>
            (props.fieldLength || 0) > 1 &&
            editable && (
              <>
                <SeparatorHorizontal $margin={16} />
                <DeleteButton
                  onClick={props.onRemove}
                  rootStyle={{ height: '32px' }}
                />
              </>
            )
          }
        </FormState>
      </ScheduleCardHeader>
      <Separator gap={24} />
      <SectionContainer>
        <HalfContainer>
          <HotelSelectInput
            name={`hotelSales.${props.parentIndex}.hotelId`}
            label={t('common:hotel')}
            onAfterDetailChange={(hotel) => {
              // setHotelRoomList(
              //   hotel.hotelRooms.map((hotelRoom) => transformer(hotelRoom)),
              // );
              setValue(
                `hotelSales.${props.parentIndex}.hotelName`,
                hotel.name || '',
              );
              setValue(
                `hotelSales.${props.parentIndex}.hotelPhone`,
                hotel.phone || '',
              );
              setValue(
                `hotelSales.${props.parentIndex}.hotelEmail`,
                hotel.email || '',
              );
              setValue(
                `hotelSales.${props.parentIndex}.hotelAddress`,
                hotel.address || '',
              );
              setValue(`hotelSales.${props.parentIndex}.defaultCheckInOut`, {
                in: hotel.checkIn || '',
                out: hotel.checkOut || '',
              });
              trigger(name);
            }}
            required
          />
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <FullContainer $style={RowBetweenStyles}>
            <FullContainer>
              <Input
                type="text"
                name={`hotelSales.${props.parentIndex}.hotelPhone`}
                label={t('common:phone_number')}
                disabled
              />
            </FullContainer>
            <SeparatorHorizontal $margin={16} />
            <FullContainer>
              <Input
                type="email"
                name={`hotelSales.${props.parentIndex}.hotelEmail`}
                label={t('common:email')}
                disabled
              />
            </FullContainer>
          </FullContainer>
        </HalfContainer>
        <HalfContainer>
          <Input
            type="text"
            name={`hotelSales.${props.parentIndex}.hotelAddress`}
            label={t('common:address')}
            disabled
          />
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <FullContainer $style={RowBetweenStyles}>
            <FullContainer>
              <Input
                type="text"
                name={`hotelSales.${props.parentIndex}.defaultCheckInOut.in`}
                label={t('common:check_in')}
                disabled
              />
            </FullContainer>
            <SeparatorHorizontal $margin={16} />
            <FullContainer>
              <Input
                type="text"
                name={`hotelSales.${props.parentIndex}.defaultCheckInOut.out`}
                label={t('common:check_out')}
                disabled
              />
            </FullContainer>
          </FullContainer>
        </HalfContainer>
        <HalfContainer>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Input
              type="switch"
              name={`hotelSales.${props.parentIndex}.isByPax`}
              noMargin
            />
            <Text variant="LabelMedium">{t('common:pricing_by_per_pax')}</Text>
          </div>
        </HalfContainer>
      </SectionContainer>
      <ScheduleGuestTable parentIndex={props.parentIndex} />
      <Separator gap={24} />
      <ScheduleAirlineTable parentIndex={props.parentIndex} />
      <Separator gap={24} />
      <ScheduleAddOnsTable parentIndex={props.parentIndex} />
      <Separator gap={12} />
      <SectionContainer>
        <HalfContainer>
          <Input
            type="check-box"
            name={`hotelSales.${props.parentIndex}.isCityTax`}
            label={t('common:city_tax')}
            onAfterChange={() =>
              //@ts-ignore
              setValue(`hotelSales.${props.parentIndex}.cityTax`, 0, {
                shouldTouch: true,
                shouldValidate: true,
              })
            }
            noMargin
          />
          <Input
            type="text"
            name={`hotelSales.${props.parentIndex}.cityTax`}
            placeholder={t('common:city_tax')}
            disabled={!isCityTax}
            required={isCityTax}
            hideRequiredStar
            min={0}
          />
        </HalfContainer>
      </SectionContainer>
    </CardSection>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});
